import api from 'api'
import { CACHE_TAG_ROUTE_OPTIMIZATION_JOB } from 'api/cacheTagTypes'

import type {
  CreateRouteOptimizationJobBody,
  ResolveRouteOptimizationJobBody,
  RouteOptimizationJobApi,
  RouteOptimizationJobResultApi,
} from 'types/api/route/routeOptimizationJob'

const JOBS_URL = '/api/v1/core/route-optimization/jobs'

export interface SubmittedRouteOptimizationJob extends RouteOptimizationJobApi {
  // 202 queued a new job; 200 joined one already in flight for the route-day.
  created: boolean
}

const extendedApi = api.injectEndpoints({
  endpoints: build => ({
    submitRouteOptimizationJob: build.mutation<SubmittedRouteOptimizationJob, CreateRouteOptimizationJobBody>({
      query: body => ({ url: JOBS_URL, method: 'POST', body }),
      transformResponse: (response: RouteOptimizationJobApi, meta) => ({
        ...response,
        created: meta?.response?.status === 202,
      }),
      // A 200 hands back a job that was already running, so its cached status may predate this submit.
      invalidatesTags: result => (result ? [{ type: CACHE_TAG_ROUTE_OPTIMIZATION_JOB, id: result.jobId }] : []),
    }),
    getRouteOptimizationJob: build.query<RouteOptimizationJobApi, string>({
      query: jobId => ({ url: `${JOBS_URL}/${jobId}`, method: 'GET' }),
      providesTags: (result, error, jobId) => [{ type: CACHE_TAG_ROUTE_OPTIMIZATION_JOB, id: jobId }],
    }),
    resolveRouteOptimizationJob: build.mutation<RouteOptimizationJobApi, { jobId: string } & ResolveRouteOptimizationJobBody>({
      query: ({ jobId, resolution }) => ({ url: `${JOBS_URL}/${jobId}/resolution`, method: 'PATCH', body: { resolution } }),
      invalidatesTags: (result, error, { jobId }) => [{ type: CACHE_TAG_ROUTE_OPTIMIZATION_JOB, id: jobId }],
    }),
    getRouteOptimizationJobResult: build.query<RouteOptimizationJobResultApi, string>({
      query: jobId => ({ url: `${JOBS_URL}/${jobId}/result`, method: 'GET' }),
      providesTags: (result, error, jobId) => [{ type: CACHE_TAG_ROUTE_OPTIMIZATION_JOB, id: `${jobId}-result` }],
    }),
  }),
})

export const {
  useSubmitRouteOptimizationJobMutation,
  useResolveRouteOptimizationJobMutation,
  useGetRouteOptimizationJobQuery,
  useGetRouteOptimizationJobResultQuery,
  useLazyGetRouteOptimizationJobQuery,
  useLazyGetRouteOptimizationJobResultQuery,
} = extendedApi
